export const DEFAULT_OFFER_POLICY = Object.freeze({
  delayMs: 25_000,
  scrollThreshold: 0.4,
  desktopExitDelayMs: 5_000,
  dismissalSuppressionMs: 30 * 24 * 60 * 60 * 1_000,
});

export const advanceActiveTime = (activeMs, elapsedMs, isVisible) => {
  const current = Number.isFinite(activeMs) ? Math.max(0, activeMs) : 0;
  const elapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  return isVisible ? current + elapsed : current;
};

export const createStorageAdapter = (storage) => {
  try {
    const probe = '__budziko_offer_probe__';
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    return {
      available: true,
      get: (key) => {
        try { return storage.getItem(key); } catch { return null; }
      },
      set: (key, value) => {
        try { storage.setItem(key, value); return true; } catch { return false; }
      },
    };
  } catch {
    return {
      available: false,
      get: () => null,
      set: () => false,
    };
  }
};

const isSuppressed = (dismissedAtMs, nowMs, suppressionMs) => (
  Number.isFinite(dismissedAtMs)
  && Number.isFinite(nowMs)
  && Math.max(0, nowMs - dismissedAtMs) < suppressionMs
);

export const decideOfferPresentation = (state, policy = DEFAULT_OFFER_POLICY) => {
  if (state.postedSuccess || state.postedError) return 'form';
  if (!state.eligible || state.submitted) return 'hidden';
  if (isSuppressed(state.dismissedAtMs, state.nowMs, policy.dismissalSuppressionMs)) {
    return 'hidden';
  }
  if (state.formVisible) return 'form';
  if (state.autoShownThisSession) return 'hidden';
  if (
    state.device === 'desktop'
    && state.exitIntent
    && state.activeMs >= policy.desktopExitDelayMs
  ) {
    return 'form';
  }
  if (state.activeMs >= policy.delayMs || state.scrollRatio >= policy.scrollThreshold) {
    return 'form';
  }
  return 'hidden';
};

export const parseStoredTimestamp = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  let value = raw;
  if (typeof raw === 'string') {
    try { value = JSON.parse(raw); } catch { value = raw; }
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }
  if (typeof value !== 'string') return null;
  if (/^\d+$/.test(value)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const createOfferController = ({
  popup,
  documentObject,
  windowObject,
  performanceObject,
  local,
  session,
}) => {
  const close = popup.querySelector('[data-close]');
  if (!close) return null;

  const id = popup.dataset.id || popup.id || 'offer';
  const dismissKey = popup.dataset.dismissKey || `fluco_popup-${id}`;
  const submitKey = popup.dataset.submitKey || `budziko_popup_submitted_${id}`;
  const autoShownKey = `budziko_popup_auto_shown_${id}`;
  const persistenceAvailable = local.available && session.available;
  const device = windowObject.matchMedia('(hover: hover) and (pointer: fine)').matches
    ? 'desktop'
    : 'mobile';
  const designMode = Boolean(windowObject.Shopify?.designMode);
  const scrollRatio = () => {
    const scrollable = Math.max(
      0,
      documentObject.documentElement.scrollHeight - windowObject.innerHeight,
    );
    if (scrollable === 0) return 0;
    return Math.min(1, Math.max(0, windowObject.scrollY / scrollable));
  };

  let activeMs = 0;
  let lastTickMs = performanceObject.now();
  let wasVisible = !documentObject.hidden;
  let maximumScrollRatio = scrollRatio();
  let dismissedAtMs = parseStoredTimestamp(local.get(dismissKey));
  let submitted = local.get(submitKey) === '1';
  let autoShownThisSession = !persistenceAvailable || session.get(autoShownKey) === '1';
  let postedSuccess = Boolean(popup.querySelector('[data-popup-posted]'));
  let postedError = Boolean(popup.querySelector('[data-popup-error]'));
  let formVisible = false;

  const setVisible = (visible) => {
    formVisible = visible;
    if (visible) popup.classList.add('visible', 'animation--revealed');
    else popup.classList.remove('visible', 'animation--revealed');
    popup.setAttribute('aria-hidden', visible ? 'false' : 'true');
  };

  const state = (overrides = {}) => ({
    eligible: popup.dataset.offerEligible !== 'false',
    submitted,
    postedSuccess,
    postedError,
    formVisible,
    autoShownThisSession,
    dismissedAtMs,
    nowMs: Date.now(),
    device,
    activeMs,
    scrollRatio: maximumScrollRatio,
    exitIntent: false,
    ...overrides,
  });

  const show = ({ automatic = false } = {}) => {
    if (automatic) {
      if (!persistenceAvailable || !session.set(autoShownKey, '1')) {
        autoShownThisSession = true;
        setVisible(false);
        return;
      }
      autoShownThisSession = true;
    }
    setVisible(true);
  };

  const evaluate = (overrides = {}) => {
    const presentation = decideOfferPresentation(state(overrides));
    if (presentation === 'form') {
      const automatic = !formVisible && !postedSuccess && !postedError;
      show({ automatic });
    } else {
      setVisible(false);
    }
  };

  const updateActiveClock = () => {
    const nowMs = performanceObject.now();
    activeMs = advanceActiveTime(activeMs, nowMs - lastTickMs, wasVisible);
    lastTickMs = nowMs;
    wasVisible = !documentObject.hidden;
  };

  const tick = () => {
    updateActiveClock();
    maximumScrollRatio = Math.max(maximumScrollRatio, scrollRatio());
    evaluate();
  };

  const dismiss = ({ persist = true } = {}) => {
    postedSuccess = false;
    postedError = false;
    setVisible(false);
    if (!persist || designMode) return;
    dismissedAtMs = Date.now();
    autoShownThisSession = true;
    local.set(dismissKey, JSON.stringify(new Date(dismissedAtMs).toISOString()));
    session.set(autoShownKey, '1');
  };

  const onCloseClick = (event) => {
    event.preventDefault();
    dismiss();
  };
  const onKeydown = (event) => {
    if (event.key === 'Escape' && formVisible) dismiss();
  };
  const onVisibilityChange = () => tick();
  const onScroll = () => tick();
  const onExitIntent = (event) => {
    if (device !== 'desktop' || event.relatedTarget || event.clientY > 0) return;
    updateActiveClock();
    evaluate({ exitIntent: true });
  };

  close.addEventListener('click', onCloseClick);
  documentObject.addEventListener('keydown', onKeydown);
  documentObject.addEventListener('visibilitychange', onVisibilityChange);
  windowObject.addEventListener('scroll', onScroll, { passive: true });
  documentObject.addEventListener('mouseout', onExitIntent);

  let timer = null;
  if (!designMode) timer = windowObject.setInterval(tick, 250);

  if (postedSuccess) {
    submitted = true;
    local.set(submitKey, '1');
    popup.classList.add('popup--posted');
  }
  evaluate();

  return {
    select: () => show(),
    deselect: () => dismiss({ persist: false }),
    destroy: () => {
      if (timer !== null) windowObject.clearInterval(timer);
      close.removeEventListener('click', onCloseClick);
      documentObject.removeEventListener('keydown', onKeydown);
      documentObject.removeEventListener('visibilitychange', onVisibilityChange);
      windowObject.removeEventListener('scroll', onScroll);
      documentObject.removeEventListener('mouseout', onExitIntent);
    },
  };
};

export const persistFooterOfferSubmission = ({ popups, local, submitted }) => {
  if (!submitted || !local.available) return false;
  return popups.every((popup) => {
    const id = popup.dataset.id || popup.id || 'offer';
    const key = popup.dataset.submitKey || `budziko_popup_submitted_${id}`;
    return local.set(key, '1');
  });
};

export const initializeBrowserBehavior = ({
  documentObject = globalThis.document,
  windowObject = globalThis.window,
  performanceObject = globalThis.performance,
} = {}) => {
  const controllers = new WeakMap();
  const createStorage = (name) => {
    try { return createStorageAdapter(windowObject[name]); } catch { return createStorageAdapter(null); }
  };
  const local = createStorage('localStorage');
  const session = createStorage('sessionStorage');

  const initializeOffer = (popup) => {
    if (controllers.has(popup)) return;
    persistFooterOfferSubmission({
      popups: [popup],
      local,
      submitted: Boolean(documentObject.querySelector('[data-footer-offer-posted]')),
    });
    const controller = createOfferController({
      popup,
      documentObject,
      windowObject,
      performanceObject,
      local,
      session,
    });
    if (controller) controllers.set(popup, controller);
  };
  const initializeOffers = (root = documentObject) => {
    if (root.matches?.('[data-offer-popup]')) initializeOffer(root);
    root.querySelectorAll?.('[data-offer-popup]').forEach(initializeOffer);
  };
  const offerFromEvent = (event) => event.target.matches?.('[data-offer-popup]')
    ? event.target
    : event.target.querySelector?.('[data-offer-popup]');

  documentObject.addEventListener('shopify:section:load', (event) => initializeOffers(event.target));
  documentObject.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-offer-popup]').forEach((popup) => {
      controllers.get(popup)?.destroy();
      controllers.delete(popup);
    });
  });
  documentObject.addEventListener('shopify:block:select', (event) => {
    const popup = offerFromEvent(event);
    if (popup) controllers.get(popup)?.select();
  });
  documentObject.addEventListener('shopify:block:deselect', (event) => {
    const popup = offerFromEvent(event);
    if (popup) controllers.get(popup)?.deselect();
  });
  initializeOffers();
};

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBrowserBehavior, { once: true });
  } else {
    initializeBrowserBehavior();
  }
}
